<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  vipLevel: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const formData = ref({
  id: null,
  level: null,
  name: '',
  icon_url: '',
  min_value: null,
  max_value: null,
  active: true,
  position: 0,
  text_color: '#FFFFFF',
  text_shadow: true,
});

const fileInput = ref(null);
const iconPreview = ref(null);
const isUploading = ref(false);

const MAX_FILE_SIZE = 200 * 1024; // 200 KB
const TARGET_ICON_SIZE = 80;
const ALLOWED_FORMATS = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/svg+xml',
];

const resetFileInput = () => {
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const resizeImageToSquare = file =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = TARGET_ICON_SIZE;
        canvas.height = TARGET_ICON_SIZE;
        const ctx = canvas.getContext('2d');
        const scale = Math.max(
          TARGET_ICON_SIZE / img.width,
          TARGET_ICON_SIZE / img.height
        );
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const dx = (TARGET_ICON_SIZE - scaledWidth) / 2;
        const dy = (TARGET_ICON_SIZE - scaledHeight) / 2;
        ctx.clearRect(0, 0, TARGET_ICON_SIZE, TARGET_ICON_SIZE);
        ctx.drawImage(img, dx, dy, scaledWidth, scaledHeight);
        const base64 = canvas.toDataURL('image/png');
        resolve(base64);
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('IMAGE_LOAD_FAILED'));
    };
    img.src = objectUrl;
  });

const validateAndProcessIcon = async file => {
  if (!ALLOWED_FORMATS.includes(file.type)) {
    useAlert(t('VIP_SETTINGS.FORM.VIP_ICON.ERRORS.INVALID_FORMAT'));
    resetFileInput();
    return null;
  }

  if (file.size > MAX_FILE_SIZE) {
    useAlert(
      t('VIP_SETTINGS.FORM.VIP_ICON.ERRORS.FILE_TOO_LARGE', { size: '200KB' })
    );
    resetFileInput();
    return null;
  }

  try {
    isUploading.value = true;
    const resizedBase64 = await resizeImageToSquare(file);
    const approxSize = Math.round((resizedBase64.length * 3) / 4);
    if (approxSize > MAX_FILE_SIZE) {
      useAlert(
        t('VIP_SETTINGS.FORM.VIP_ICON.ERRORS.FILE_TOO_LARGE', { size: '200KB' })
      );
      resetFileInput();
      return null;
    }
    return resizedBase64;
  } catch (error) {
    useAlert(t('VIP_SETTINGS.FORM.VIP_ICON.ERRORS.PROCESS_FAILED'));
    resetFileInput();
    return null;
  } finally {
    isUploading.value = false;
  }
};

const handleFileSelect = async event => {
  const file = event.target.files[0];
  if (!file) return;

  const processedIcon = await validateAndProcessIcon(file);
  if (!processedIcon) {
    return;
  }

  iconPreview.value = processedIcon;
  formData.value.icon_url = processedIcon;
};

const removeIcon = () => {
  iconPreview.value = null;
  formData.value.icon_url = '';
  resetFileInput();
};

const syncFormData = source => {
  formData.value = { ...source };
  iconPreview.value = source.icon_url;
};

onMounted(() => {
  syncFormData(props.vipLevel);
});

watch(
  () => props.vipLevel,
  newVal => {
    if (newVal) {
      syncFormData(newVal);
    }
  },
  { deep: true }
);

const handleSubmit = () => {
  if (!formData.value.level || !formData.value.name) {
    useAlert(t('VIP_SETTINGS.FORM.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  if (formData.value.min_value && formData.value.max_value) {
    if (formData.value.max_value <= formData.value.min_value) {
      useAlert(t('VIP_SETTINGS.FORM.VALIDATION.INVALID_RANGE'));
      return;
    }
  }

  emit('submit', { id: formData.value.id, vip_level: formData.value });
};
</script>

<template>
  <div class="vip-modal flex flex-col h-auto overflow-auto max-w-2xl mx-auto">
    <div class="vip-modal__header p-6">
      <h2 class="vip-modal__title">
        {{ $t('VIP_SETTINGS.EDIT.TITLE') }}
      </h2>
      <p class="vip-modal__subtitle">
        {{ $t('VIP_SETTINGS.EDIT.DESC') }}
      </p>
    </div>

    <form
      class="flex flex-col w-full p-6 space-y-4"
      @submit.prevent="handleSubmit"
    >
      <!-- Level -->
      <label class="flex flex-col gap-1">
        <span class="vip-label">
          {{ $t('VIP_SETTINGS.FORM.LEVEL.LABEL') }}
          <span class="text-n-ruby-9">*</span>
        </span>
        <input
          v-model.number="formData.level"
          type="number"
          min="1"
          class="vip-input w-full"
          :placeholder="$t('VIP_SETTINGS.FORM.LEVEL.PLACEHOLDER')"
          required
        />
      </label>

      <!-- Name -->
      <label class="flex flex-col gap-1">
        <span class="vip-label">
          {{ $t('VIP_SETTINGS.FORM.NAME.LABEL') }}
          <span class="text-n-ruby-9">*</span>
        </span>
        <input
          v-model="formData.name"
          type="text"
          class="vip-input w-full"
          :placeholder="$t('VIP_SETTINGS.FORM.NAME.PLACEHOLDER')"
          required
        />
      </label>

      <!-- Range (Optional) -->
      <div class="flex flex-col gap-2">
        <div class="flex gap-4">
          <label class="flex flex-col gap-1 flex-1">
            <span class="vip-label">
              {{ $t('VIP_SETTINGS.FORM.MIN_VALUE.LABEL') }}
            </span>
            <input
              v-model.number="formData.min_value"
              type="number"
              min="0"
              class="vip-input w-full"
              :placeholder="$t('VIP_SETTINGS.FORM.MIN_VALUE.PLACEHOLDER')"
            />
          </label>

          <label class="flex flex-col gap-1 flex-1">
            <span class="vip-label">
              {{ $t('VIP_SETTINGS.FORM.MAX_VALUE.LABEL') }}
            </span>
            <input
              v-model.number="formData.max_value"
              type="number"
              min="0"
              class="vip-input w-full"
              :placeholder="$t('VIP_SETTINGS.FORM.MAX_VALUE.PLACEHOLDER')"
            />
          </label>
        </div>
        <p class="vip-hint">
          {{ $t('VIP_SETTINGS.FORM.MIN_VALUE.HELP') }}
        </p>
      </div>

      <!-- Icon Upload -->
      <div class="flex flex-col gap-2">
        <span class="vip-label">
          {{ $t('VIP_SETTINGS.FORM.ICON.LABEL') }}
        </span>

        <div v-if="iconPreview" class="flex items-center gap-4">
          <img
            :src="iconPreview"
            class="w-20 h-20 rounded-lg shadow-md object-cover border border-n-weak"
            alt="Preview"
          />
          <NextButton
            type="button"
            icon="i-lucide-trash-2"
            :label="$t('VIP_SETTINGS.FORM.ICON.REMOVE')"
            ruby
            faded
            sm
            @click="removeIcon"
          />
        </div>

        <input
          ref="fileInput"
          type="file"
          accept=".png,.jpg,.jpeg,.svg"
          class="hidden"
          @change="handleFileSelect"
        />

        <NextButton
          v-if="!iconPreview"
          type="button"
          icon="i-lucide-upload"
          :label="$t('VIP_SETTINGS.FORM.ICON.UPLOAD')"
          slate
          sm
          :is-loading="isUploading"
          :disabled="isUploading"
          @click="fileInput.click()"
        />

        <p class="text-xs text-n-subtitle">
          {{
            $t('VIP_SETTINGS.FORM.ICON.HINT', {
              size: TARGET_ICON_SIZE,
              maxSize: '200KB',
            })
          }}
        </p>
      </div>

      <!-- Text Styling -->
      <div class="vip-styling-box flex flex-col gap-3 p-4 border rounded-lg">
        <h3 class="vip-label">
          {{ $t('VIP_SETTINGS.FORM.TEXT_STYLING.TITLE') }}
        </h3>

        <!-- Text Color -->
        <label class="flex items-center gap-3">
          <span class="text-sm min-w-[120px]" style="color: #111827">
            {{ $t('VIP_SETTINGS.FORM.TEXT_STYLING.COLOR') }}
          </span>
          <input
            v-model="formData.text_color"
            type="color"
            class="w-16 h-8 border border-n-weak rounded cursor-pointer"
          />
          <span class="text-xs" style="color: #4b5563">{{
            formData.text_color
          }}</span>
        </label>

        <!-- Text Shadow -->
        <label class="flex items-center gap-3">
          <input
            v-model="formData.text_shadow"
            type="checkbox"
            class="w-4 h-4"
          />
          <span class="text-sm text-n-title">
            {{ $t('VIP_SETTINGS.FORM.TEXT_STYLING.SHADOW') }}
          </span>
        </label>

        <!-- Preview -->
        <div
          v-if="iconPreview"
          class="flex items-center gap-3 pt-2 border-t border-n-weak"
        >
          <span class="text-xs min-w-[120px]" style="color: #4b5563">
            {{ $t('VIP_SETTINGS.FORM.TEXT_STYLING.PREVIEW') }}
          </span>
          <div class="relative w-20 h-20">
            <img
              :src="iconPreview"
              class="w-20 h-20 rounded-lg object-cover"
              alt="Preview"
            />
            <span
              class="absolute inset-0 flex items-center justify-center text-xl font-extrabold"
              :style="{
                color: formData.text_color,
                textShadow: formData.text_shadow
                  ? '0 1px 2px rgba(0,0,0,0.3)'
                  : 'none',
              }"
            >
              {{ formData.level }}
            </span>
          </div>
        </div>
      </div>

      <!-- Active Status -->
      <label class="flex items-center gap-2">
        <input v-model="formData.active" type="checkbox" class="w-4 h-4" />
        <span class="text-sm" style="color: #111827">
          {{ $t('VIP_SETTINGS.FORM.ACTIVE.LABEL') }}
        </span>
      </label>

      <!-- Actions -->
      <div class="flex items-center justify-end gap-2 pt-4">
        <NextButton
          type="button"
          :label="$t('VIP_SETTINGS.EDIT.CANCEL')"
          slate
          @click="emit('cancel')"
        />
        <NextButton
          type="submit"
          :label="$t('VIP_SETTINGS.EDIT.SUBMIT')"
          onyx
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
.vip-modal {
  background: #ffffff;
}
.vip-modal__header {
  border-bottom: 1px solid #e5e7eb;
}
.vip-modal__title {
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
}
.vip-modal__subtitle {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
}
.vip-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px;
  display: block;
}
.vip-input {
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  outline: none;
  transition: border-color 0.15s;
}
.vip-input:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}
.vip-input::placeholder {
  color: #9ca3af;
}
.vip-hint {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}
.vip-styling-box {
  background: #f9fafb;
  border-color: #e5e7eb;
}

:deep(.text-n-title) {
  color: #111827 !important;
}
:deep(.text-n-subtitle) {
  color: #4b5563 !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-slate-1) {
  background: #ffffff !important;
}
</style>
